import storage from 'redux-persist/lib/storage';
import adminArticleSuggestionReducer from '../features/admin/slices/adminArticleSuggestionReducer';
import adminBlogReducer from '../features/admin/slices/adminBlogModerationReducer';
import adminCommentReducer from '../features/admin/slices/adminCommentModerationReducer';
import adminContactUsReducer from '../features/admin/slices/adminContactUsReducer';
import adminRecentBlogsReducer from '../features/admin/slices/adminRecentBlogsReducer';
import terrainReducer from '../features/terrain/slices/terrainSlice';
import fragmentReducer from '../features/terrain/slices/fragmentSlice';
import clearingReducer from '../features/terrain/slices/clearingSlice';
import topClearingsReducer from '../features/terrain/slices/topClearingsSlice';
import recommendationsReducer from '../features/recommendations/slices/recommendationsSlice';
import authSlice from '../features/auth/slices/authSlice';
import blogPageSlice from '../features/blogs/slices/blogPageReducer';
import blogProfileTagListReducer from '../features/blogs/slices/lists/blogProfileTagListReducer';
import blogRecentListReducer from '../features/blogs/slices/lists/blogRecentListReducer';
import blogShuffledListSlice from '../features/blogs/slices/lists/blogShuffledListReducer';
import curatedListsSlice from '../features/curatedLists/slices/curatedListsSlice';
import discussionReducer from '../features/discussions/slices/discussionReducer';
import donationSlice from '../features/donations/slices/donationSlice';
import articleFeaturedListReducer from '../features/featuredArticles/slices/articleFeaturedListReducer';
import articleFeaturedAllReducer from '../features/featuredArticles/slices/articleFeaturedAllReducer';
import featuredArticleDetailSlice from '../features/featuredArticles/slices/featuredArticlePageReducer';
import navSlice from '../features/navigation/slices/navigationReducer';
import myReadsSlice from '../features/myReads/slices/myReadsReducer';
import notificationsSlice from '../features/notifications/slices/notificationsReducer';
import profileSlice from '../features/profile/slices/profileSlice';
import publicProfileSlice from '../features/publicProfile/slices/publicProfileReducer';
import reviewsSlice from '../features/reviews/slices/reviewsSlice';
import searchSlice from '../features/search/searchSlice';
import tagSlice from '../features/tags/slices/tagReducer';
import blogUpvoteSlice from '../features/upvotes/slices/blogUpvoteReducer';
import commentUpvoteReducer from '../features/upvotes/slices/commentUpvoteReducer';
import discussionUpvoteReducer from '../features/upvotes/slices/discussionUpvoteReducer';
import featuredArticleUpvoteSlice from '../features/upvotes/slices/featuredArticleUpvoteReducer';

import { combineReducers, configureStore } from '@reduxjs/toolkit';
import {
  FLUSH,
  PAUSE,
  PERSIST,
  persistReducer,
  persistStore,
  PURGE,
  REGISTER,
  REHYDRATE,
} from 'redux-persist';

const rootReducer = combineReducers({
  user: authSlice,
  tag: tagSlice,
  blogList: combineReducers({
    shuffled: blogShuffledListSlice,
    recent: blogRecentListReducer,
    profileTag: blogProfileTagListReducer,
  }),
  blogPage: blogPageSlice,
  articleFeatured: articleFeaturedListReducer,
  articleFeaturedAll: articleFeaturedAllReducer,
  featuredArticleUpvote: featuredArticleUpvoteSlice,
  featuredArticleDetail: featuredArticleDetailSlice,
  nav: navSlice,
  searchResults: searchSlice,
  discussion: discussionReducer,
  blogUpvote: blogUpvoteSlice,
  discussionUpvote: discussionUpvoteReducer,
  commentUpvote: commentUpvoteReducer,
  notifications: notificationsSlice,
  myReads: myReadsSlice,
  profile: profileSlice,
  publicProfile: publicProfileSlice,
  adminBlogs: adminBlogReducer,
  adminComments: adminCommentReducer,
  adminContactUs: adminContactUsReducer,
  adminRecentBlogs: adminRecentBlogsReducer,
  adminArticleSuggestions: adminArticleSuggestionReducer,
  donations: donationSlice,
  reviews: reviewsSlice,
  curatedLists: curatedListsSlice,
  terrain: terrainReducer,
  fragments: fragmentReducer,
  clearing: clearingReducer,
  topClearings: topClearingsReducer,
  recommendations: recommendationsReducer,
});

const persistConfig = {
  key: 'root',
  storage: storage,
  blacklist: ['upvote', 'discussionUpvote', 'curatedLists', 'terrain', 'fragments', 'clearing', 'topClearings', 'recommendations'],
};

const persistedReducer = persistReducer(persistConfig, rootReducer);

export const store = configureStore({
  reducer: persistedReducer,
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }),
});

export type AppState = ReturnType<typeof rootReducer>;
export type AppDispatch = typeof store.dispatch;

const STORE_VERSION = '2.4.0';

export const persistor = persistStore(store, null, () => {
  const currentVersion = localStorage.getItem('reduxVersion');

  if (currentVersion !== STORE_VERSION) {
    persistor.purge();
    localStorage.setItem('reduxVersion', STORE_VERSION);
  }
});
